/* Humbler header: shared dialog helpers, announcement rotator, menu drawer, desktop menu panels, bag count (search: humbler-search.js). */
(() => {
  if (window.HumblerUI) return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isPreview = document.documentElement.dataset.humblerPreview === 'true';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------- dialogs ---------- */
  let lastTrigger = null;
  // Lock page scroll while any dialog is open. Hiding the scrollbar would widen the page (content reflows and
  // the scroll position nudges), so the width it frees is padded back; with overlay scrollbars nothing changes.
  // The gutter is measured and padded back before the lock applies: no layout ever runs at the wider width
  // (one did, and browser scroll anchoring then moved the page down on every open).
  const lockScroll = () => {
    const root = document.documentElement;
    const lock = !!document.querySelector('dialog.hb-dialog[open]');
    if (lock === root.classList.contains('hb-scroll-lock')) return;
    if (lock) {
      const gap = window.innerWidth - root.clientWidth;
      if (gap > 0) root.style.setProperty('--hb-scrollbar-gap', `${gap}px`);
      root.classList.add('hb-scroll-lock');
    } else {
      root.classList.remove('hb-scroll-lock');
      root.style.removeProperty('--hb-scrollbar-gap');
    }
  };
  function openDialog(dialog, trigger) {
    if (!dialog || dialog.open) return;
    lastTrigger = trigger || document.activeElement;
    const pageX = window.scrollX;
    const pageY = window.scrollY;
    dialog.showModal();
    // showModal() focuses the first control while the panel is still sliding in from off-screen; never let
    // that scroll the dialog box itself (it made drawers flash in from the middle) or the page behind it.
    dialog.scrollLeft = 0;
    dialog.scrollTop = 0;
    if (window.scrollX !== pageX || window.scrollY !== pageY) window.scrollTo(pageX, pageY);
    // A click-opened dialog starts on its panel, not with a focus ring on the close button.
    const panel = dialog.querySelector('[data-hb-dialog-focus], .hb-dialog__panel');
    if (panel && document.activeElement?.matches('[data-hb-close]')) {
      panel.tabIndex = -1;
      panel.focus({ preventScroll: true });
    }
    lockScroll();
    document.querySelectorAll(`[aria-controls="${dialog.id}"]`).forEach(el => el.setAttribute('aria-expanded', 'true'));
    dialog.dispatchEvent(new CustomEvent('hb:open'));
  }
  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
  }
  document.addEventListener('close', event => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('hb-dialog')) return;
    lockScroll();
    document.querySelectorAll(`[aria-controls="${dialog.id}"]`).forEach(el => el.setAttribute('aria-expanded', 'false'));
    dialog.dispatchEvent(new CustomEvent('hb:close'));
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
    lastTrigger = null;
  }, true);
  document.addEventListener('click', event => {
    const closer = event.target.closest('[data-hb-close]');
    if (closer) { closeDialog(closer.closest('dialog')); return; }
    // Click on the backdrop (the dialog element itself, outside any panel) closes it.
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('hb-dialog')) closeDialog(event.target);
  });

  /* ---------- money (presentment currency) ----------
     sections/humbler-header.liquid prints window.HumblerMoney = { sample, currency, shopCurrency }, where
     sample is `123456 | money` in the shopper's currency ("$1,234.56", "Rs. 1,235", "€1.234,56",
     "1.234,56 €"). Its prefix, suffix, separators and decimals become the format; whole amounts drop
     ".00" (our style, same as money_without_trailing_zeros). Offer amounts from theme settings are in the
     shop currency (USD): fromUsd() converts them with Shopify's own rate (window.Shopify.currency.rate),
     the rate Shopify uses for automatic-discount fixed amounts. */
  const USD_FORMAT = Object.freeze({ prefix: '$', suffix: '', thousands: ',', decimal: '.', decimals: 2 });
  const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', euro: '€', pound: '£', yen: '¥', cent: '¢', curren: '¤' };
  const decodeEntities = text => String(text).replace(/&(#x[\da-f]+|#\d+|[a-z]+);/gi, (match, code) => {
    if (code[0] !== '#') return ENTITIES[code.toLowerCase()] ?? match;
    const point = code[1] === 'x' || code[1] === 'X' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
    return Number.isFinite(point) ? String.fromCodePoint(point) : match;
  });
  function parseMoneyFormat(sample) {
    const text = decodeEntities(String(sample ?? '').replace(/<[^>]*>/g, '')).trim();
    const first = text.search(/\d/);
    if (first === -1) return USD_FORMAT;
    let last = text.length - 1;
    while (last > first && !/\d/.test(text[last])) last--;
    const body = text.slice(first, last + 1);
    const groups = body.split(/\D+/);
    const separators = body.match(/\D+/g) || [];
    // Spaces around the amount become no-break spaces so "Rs. 8,257" never wraps between symbol and number.
    const glue = part => part.replace(/[ \t]/g, '\u00a0');
    const format = { prefix: glue(text.slice(0, first)), suffix: glue(text.slice(last + 1)), thousands: '', decimal: '.', decimals: 0 };
    if (separators.length) {
      // 123456 cents: a last group of 3 digits is "1,235" (no decimals); otherwise it is the cents.
      const tail = groups[groups.length - 1];
      if (tail.length === 3) format.thousands = separators[0];
      else {
        format.decimals = tail.length;
        format.decimal = separators[separators.length - 1];
        format.thousands = separators.length > 1 ? separators[0] : '';
      }
    }
    return format;
  }
  function formatMoney(cents, format = USD_FORMAT) {
    const value = Math.round(Number(cents) || 0);
    const sign = value < 0 ? '-' : '';
    const units = Math.abs(value) / 100;
    let [whole, fraction = ''] = (format.decimals > 0 ? units.toFixed(format.decimals) : String(Math.round(units))).split('.');
    whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousands);
    if (/^0*$/.test(fraction)) fraction = '';
    return `${sign}${format.prefix}${whole}${fraction ? format.decimal + fraction : ''}${format.suffix}`;
  }
  const moneyConfig = window.HumblerMoney || {};
  const moneyFormat = moneyConfig.sample ? parseMoneyFormat(moneyConfig.sample) : USD_FORMAT;
  const money = cents => formatMoney(cents, moneyFormat);
  const moneyUsd = cents => formatMoney(cents, USD_FORMAT);
  const currency = window.Shopify?.currency || {};
  // Converted when the shopper's currency is not the shop's (USD); Liquid printed a USD fallback then.
  const localized = !!(moneyConfig.currency && moneyConfig.shopCurrency && moneyConfig.currency !== moneyConfig.shopCurrency);
  const rate = (() => {
    if (currency.active && moneyConfig.shopCurrency && currency.active === moneyConfig.shopCurrency) return 1;
    const value = Number(currency.rate);
    return Number.isFinite(value) && value > 0 ? value : 1;
  })();
  const fromUsd = cents => Math.round((Number(cents) || 0) * rate);
  // A converted price to read (a builder's set price): whole units in another currency; exact in the shop's.
  const fromUsdWhole = cents => (rate === 1 ? fromUsd(cents) : Math.round(fromUsd(cents) / 100) * 100);

  /* Offer amounts in Liquid (snippets/humbler-offer-money and the guarded offer math) carry data
     attributes; in another currency they are filled in here, on load and for content added later:
     data-hb-usd="9900"                 money(fromUsd(9900))
     data-hb-usd-whole                  with it: money(fromUsdWhole(9900)), a price to read, whole units
     data-hb-usd-each="3"               with it: money(fromUsd(9900 / 3) × 3), the chain deal as checkout charges it
     data-hb-usd-if="gt:13000:9900"     shown only when 13000 (shopper's currency) > fromUsd(9900);
                                        gt, gte, lt, lte (Liquid renders it hidden) */
  const tests = { gt: (a, b) => a > b, gte: (a, b) => a >= b, lt: (a, b) => a < b, lte: (a, b) => a <= b };
  function localizeOffers(scope = document) {
    if (!localized || !scope.querySelectorAll) return;
    const each = (selector, apply) => {
      const nodes = [...scope.querySelectorAll(selector)];
      if (scope.matches?.(selector)) nodes.unshift(scope);
      nodes.forEach(node => { if (!node.hasAttribute('data-hb-local')) { node.setAttribute('data-hb-local', ''); apply(node); } });
    };
    each('[data-hb-usd]', node => {
      const parts = Math.max(1, Math.round(Number(node.dataset.hbUsdEach) || 1));
      const convert = node.hasAttribute('data-hb-usd-whole') ? fromUsdWhole : fromUsd;
      node.textContent = money(convert(Math.round(Number(node.dataset.hbUsd) / parts)) * parts);
    });
    each('[data-hb-usd-if]', node => {
      const [op, from, usd] = node.dataset.hbUsdIf.split(':');
      node.hidden = !(tests[op] || tests.gt)(Number(from), fromUsd(Number(usd)));
    });
  }
  if (localized) {
    const start = () => {
      localizeOffers(document);
      new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => { if (node.nodeType === 1) localizeOffers(node); })))
        .observe(document.documentElement, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  }
  const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const imageUrl = (src, width) => {
    if (!src) return '';
    try { const url = new URL(src, location.href); url.searchParams.set('width', width); return url.href; } catch { return src; }
  };
  let previewData = null;
  // Shared across scripts; a failed load is not kept, so a retry fetches again.
  const loadPreviewData = () => (previewData ||= fetch('/preview-data.json').then(response => {
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }).catch(error => { previewData = null; throw error; }));

  window.HumblerUI = {
    openDialog, closeDialog, money, moneyUsd, fromUsd, fromUsdWhole, localized, localizeOffers, parseMoneyFormat, formatMoney,
    escape, imageUrl, isPreview, loadPreviewData, reduceMotion
  };

  /* ---------- announcement ---------- */
  // Rotates the bar's messages and never cuts one off. A message wider than its slot reads from its start for
  // most of the interval, glides once to its end (about 34px/s), rests there for GLIDE_HOLD and then the next
  // message comes, so the cut start never lingers. While paused, under reduced motion, with a single message or
  // while rotation is held (mouse hover, keyboard focus, a block selected in the theme editor) it wraps onto two
  // lines inside the same bar height instead, so a held message is always whole.
  const GLIDE_HOLD = 1200;
  const GLIDE_SPEED = 34 / 1000;
  class HumblerAnnouncement extends HTMLElement {
    connectedCallback() {
      if (!this.messages) this.setup();
      reduceMotion.addEventListener('change', this.refit);
      this.width = Math.round(this.track.getBoundingClientRect().width);
      this.resizeObserver.observe(this.track);
      this.refit();
      if (document.fonts) document.fonts.ready.then(() => { if (this.isConnected) this.refit(); });
    }
    disconnectedCallback() {
      this.stop();
      this.resizeObserver.disconnect();
      reduceMotion.removeEventListener('change', this.refit);
      if (this.glide) this.glide.cancel();
    }
    setup() {
      this.messages = [...this.querySelectorAll('.hb-announcement__message')];
      this.track = this.querySelector('.hb-announcement__track');
      this.rotating = this.hasAttribute('data-rotating') && this.messages.length > 1;
      this.index = Math.max(0, this.messages.findIndex(message => !message.hidden));
      this.paused = reduceMotion.matches;
      this.holds = new Set();
      this.pauseButton = this.querySelector('.hb-announcement__pause');
      if (this.pauseButton) this.pauseButton.setAttribute('aria-pressed', String(this.paused));
      this.refit = () => { this.fit(); this.schedule(); };
      // A hold wraps a long message and its release glides it again from the start, so both refit.
      const hold = (reason, on) => {
        const held = this.holds.size > 0;
        this.holds[on ? 'add' : 'delete'](reason);
        if (held !== this.holds.size > 0) this.refit(); else this.schedule();
      };
      this.addEventListener('click', event => {
        const step = event.target.closest('[data-step]');
        if (step) this.show(this.index + Number(step.dataset.step));
        if (event.target.closest('.hb-announcement__pause')) {
          this.paused = !this.paused;
          this.pauseButton.setAttribute('aria-pressed', String(this.paused));
          this.refit();
        }
      });
      // Only a hovering pointer holds: a tap's enter/leave pair would flash a long message wrapped.
      this.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') hold('pointer', true); });
      this.addEventListener('pointerleave', event => { if (event.pointerType !== 'touch') hold('pointer', false); }); // a tap must not restart the timer
      // Keyboard focus holds the rotation; a tap or click on the arrows does not leave it stuck.
      this.addEventListener('focusin', event => hold('focus', event.target.matches(':focus-visible')));
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) hold('focus', false); });
      this.addEventListener('shopify:block:select', event => {
        const index = this.messages.indexOf(event.target);
        if (index > -1) { this.show(index); hold('editor', true); }
      });
      this.addEventListener('shopify:block:deselect', () => hold('editor', false));
      this.resizeObserver = new ResizeObserver(([entry]) => {
        const width = Math.round(entry.contentRect.width);
        if (width === this.width) return;
        this.width = width;
        this.refit();
      });
    }
    show(index) {
      if (this.messages.length < 2) return;
      this.index = (index + this.messages.length) % this.messages.length;
      this.messages.forEach((message, position) => {
        message.hidden = position !== this.index;
        message.classList.toggle('is-entering', position === this.index);
      });
      this.refit();
    }
    // Short messages stay centred; a long one glides while the bar plays and wraps when it cannot move.
    fit() {
      if (this.glide) this.glide.cancel();
      this.glide = null;
      const message = this.messages[this.index];
      const text = message && message.firstElementChild;
      if (!text) return;
      message.classList.remove('is-gliding', 'is-wrapped');
      const style = getComputedStyle(message);
      const room = message.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      if (text.getBoundingClientRect().width <= room + .01) return;
      if (!this.rotating || this.paused || this.holds.size || reduceMotion.matches) { message.classList.add('is-wrapped'); return; }
      message.classList.add('is-gliding');
      const distance = Math.ceil(text.getBoundingClientRect().width - message.clientWidth);
      if (distance <= 0) return;
      const travel = Math.max(600, distance / GLIDE_SPEED);
      // The start (the words that matter most) takes whatever the interval leaves; the end rests GLIDE_HOLD.
      const start = Math.max(GLIDE_HOLD, this.interval - travel - GLIDE_HOLD);
      const duration = start + travel + GLIDE_HOLD;
      const end = `translateX(${-distance}px)`;
      this.glide = text.animate([
        { transform: 'translateX(0)', offset: 0 },
        { transform: 'translateX(0)', offset: start / duration, easing: 'cubic-bezier(.45, 0, .55, 1)' },
        { transform: end, offset: (start + travel) / duration },
        { transform: end, offset: 1 }
      ], { duration, fill: 'forwards' });
    }
    get interval() { return Number(this.dataset.interval) || 5000; }
    stop() { clearTimeout(this.timer); }
    // The next message comes once the interval and any glide (with its end rest) are both over. A glide spans
    // the interval when it can, so the timer lands just as its end rest finishes.
    schedule() {
      this.stop();
      if (!this.rotating || this.paused || this.holds.size || !this.isConnected) return;
      const timing = this.glide && this.glide.effect.getComputedTiming();
      const glideLeft = timing ? Math.max(0, timing.endTime - (timing.localTime || 0)) : 0;
      this.timer = setTimeout(() => this.show(this.index + 1), Math.max(this.interval, glideLeft));
    }
  }
  if (!customElements.get('humbler-announcement')) customElements.define('humbler-announcement', HumblerAnnouncement);

  /* ---------- navigation helpers ---------- */
  // Lazy images in a closed panel load on first open; prewarm() starts them as soon as intent shows.
  const prewarm = scope => scope?.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });
  const restart = (element, className) => { element.classList.remove(className); void element.offsetWidth; element.classList.add(className); };
  // Calls done() once the element's own animation ends, or after `timeout` (reduced motion, hidden tabs).
  // Returns a cancel function.
  const settle = (element, done, timeout) => {
    let over = false;
    const stop = () => {
      over = true;
      clearTimeout(timer);
      element.removeEventListener('animationend', finish);
      element.removeEventListener('animationcancel', finish);
    };
    const finish = event => {
      if (over || (event && event.target !== element)) return;
      stop();
      done();
    };
    const timer = setTimeout(finish, timeout);
    element.addEventListener('animationend', finish);
    element.addEventListener('animationcancel', finish);
    return stop;
  };

  /* ---------- menu drawer ----------
     sections/humbler-header.liquid: one panel of stacked views, the root and one sub-view per parent link. A
     sub-view slides over the root inside the panel; the root is inert behind it. Escape (the dialog's native
     cancel) closes the whole drawer, and hb:close resets every view. */
  const leaving = new WeakMap();
  const menuRoot = menu => menu?.querySelector('[data-hb-menu-root]');
  function resetView(view) {
    leaving.get(view)?.();
    leaving.delete(view);
    view.hidden = true;
    view.inert = false;
    view.classList.remove('is-entering', 'is-leaving');
  }
  function openSubview(trigger) {
    const menu = trigger.closest('dialog');
    const view = document.getElementById(trigger.dataset.hbMenuSub);
    if (!menu || !view) return;
    menu.querySelectorAll('[data-hb-menu-panel]').forEach(other => { if (other !== view) resetView(other); });
    menu.querySelectorAll('[data-hb-menu-sub]').forEach(button => button.setAttribute('aria-expanded', String(button === trigger)));
    resetView(view);
    prewarm(view);
    view.hidden = false;
    view.querySelectorAll('[data-hb-menu-scroll], .hb-menu__strip-track').forEach(scroller => { scroller.scrollTop = 0; scroller.scrollLeft = 0; });
    if (!reduceMotion.matches) restart(view, 'is-entering');
    const root = menuRoot(menu);
    if (root) root.inert = true;
    view.querySelector('[data-hb-menu-back]')?.focus({ preventScroll: true });
  }
  function closeSubview(view) {
    const menu = view.closest('dialog');
    const root = menuRoot(menu);
    const trigger = menu?.querySelector(`[data-hb-menu-sub="${view.id}"]`);
    if (root) root.inert = false;
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus({ preventScroll: true });
    if (reduceMotion.matches) { resetView(view); return; }
    view.inert = true;
    view.classList.remove('is-entering');
    restart(view, 'is-leaving');
    leaving.set(view, settle(view, () => { leaving.delete(view); resetView(view); }, 260));
  }
  // The drawer's search row is a button (showModal never raises the iOS keyboard). It closes the drawer and
  // opens search within the same click, focusing the header search icon first: that becomes the element
  // search returns focus to when it closes.
  function openSearchFromMenu(button) {
    const menu = button.closest('dialog');
    const headerSearch = document.querySelector('[data-hb-header] [data-hb-search-open]');
    if (!window.HumblerSearch || !document.getElementById('hb-search')) {
      window.location.href = button.dataset.searchUrl || `${root}search`;
      return;
    }
    closeDialog(menu);
    headerSearch?.focus({ preventScroll: true });
    window.HumblerSearch.open(headerSearch);
  }

  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-hb-menu-open]');
    if (opener) { event.preventDefault(); openDialog(document.getElementById('hb-menu'), opener); return; }
    const search = event.target.closest('[data-hb-menu-search]');
    if (search) { openSearchFromMenu(search); return; }
    const subTrigger = event.target.closest('[data-hb-menu-sub]');
    if (subTrigger) { openSubview(subTrigger); return; }
    const back = event.target.closest('[data-hb-menu-back]');
    const view = back?.closest('[data-hb-menu-panel]');
    if (view) closeSubview(view);
  });
  document.addEventListener('pointerdown', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-hb-menu-open]')) prewarm(menuRoot(document.getElementById('hb-menu')));
    const subTrigger = target?.closest('[data-hb-menu-sub]');
    if (subTrigger) prewarm(document.getElementById(subTrigger.dataset.hbMenuSub));
  }, { passive: true });
  document.addEventListener('focusin', event => {
    if (event.target instanceof Element && event.target.closest('[data-hb-menu-open]')) prewarm(menuRoot(document.getElementById('hb-menu')));
  });
  document.addEventListener('hb:close', event => {
    const menu = event.target;
    if (menu.id !== 'hb-menu') return;
    menu.querySelectorAll('[data-hb-menu-panel]').forEach(resetView);
    menu.querySelectorAll('[data-hb-menu-sub]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    const rootView = menuRoot(menu);
    if (rootView) {
      rootView.inert = false;
      rootView.querySelectorAll('[data-hb-menu-scroll]').forEach(scroller => { scroller.scrollTop = 0; });
    }
  }, true);

  /* ---------- desktop menu panels (≥1000px) ----------
     Disclosure buttons in the bar ([data-hb-mega-trigger], aria-controls → .hb-mega). Mouse: opens after
     120ms of hover, switches between triggers, closes 200ms after leaving the header (or 120ms on a plain bar
     link); a click pins it open, a second click closes it. Touch: a tap toggles. Keyboard: Enter/Space toggle,
     ArrowDown/Up open and move into the panel, Left/Right/Home/End move across the bar, Escape closes and
     returns focus, Tab out closes. Scrolling the page closes it too (the panel rides on the sticky header). Any
     HumblerUI dialog opening closes it. No scroll lock, no layout shift. */
  const desktop = window.matchMedia('(min-width: 1000px)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 200;
  const LINK_CLOSE_DELAY = 120;
  const mega = { trigger: null, mode: null, openTimer: 0, closeTimer: 0, scrollY: 0 };
  const SCROLL_CLOSE = 8;
  const fades = new WeakMap();
  const panelFor = trigger => (trigger ? document.getElementById(trigger.getAttribute('aria-controls')) : null);
  // The last element focused in the header (bar, open panel, Menu buttons). Crossing 1000px hides the bar (or
  // the mobile Menu button) with CSS, and Chrome can blur the focused element to <body> before the resize and
  // matchMedia handlers run, so it is remembered here: focusin sets it, focusout clears it only when focus
  // moves to an element outside the header (null is that blur, or the window losing focus), and a pointerdown
  // clears it (a click on something focusable sets it again).
  let headerFocus = null;
  const focusLost = () => !document.activeElement || document.activeElement === document.body;
  const megaScrim = () => document.querySelector('[data-hb-mega-scrim]');
  const clearOpenTimer = () => { clearTimeout(mega.openTimer); mega.openTimer = 0; };
  const clearCloseTimer = () => { clearTimeout(mega.closeTimer); mega.closeTimer = 0; };
  const showLayer = (element, className) => {
    fades.get(element)?.();
    fades.delete(element);
    element.classList.remove('is-opening', 'is-switching', 'is-closing');
    element.hidden = false;
    if (className && !reduceMotion.matches) restart(element, className);
  };
  const hideLayer = (element, animate) => {
    fades.get(element)?.();
    fades.delete(element);
    element.classList.remove('is-opening', 'is-switching');
    if (!animate || reduceMotion.matches || element.hidden) {
      element.classList.remove('is-closing');
      element.hidden = true;
      return;
    }
    element.classList.add('is-closing');
    fades.set(element, settle(element, () => { fades.delete(element); element.classList.remove('is-closing'); element.hidden = true; }, 200));
  };

  function megaOpen(trigger, { mode = 'pinned', focus = null } = {}) {
    clearOpenTimer();
    clearCloseTimer();
    const panel = panelFor(trigger);
    if (!panel || !desktop.matches || !trigger.getClientRects().length) return;
    const previous = mega.trigger;
    if (previous !== trigger) {
      if (previous) {
        const old = panelFor(previous);
        if (old) hideLayer(old, false);
        previous.setAttribute('aria-expanded', 'false');
      }
      prewarm(panel);
      placeMega(trigger);
      showLayer(panel, previous ? 'is-switching' : 'is-opening');
      panel.scrollTop = 0;
      trigger.setAttribute('aria-expanded', 'true');
      const scrim = megaScrim();
      if (scrim && (scrim.hidden || scrim.classList.contains('is-closing'))) showLayer(scrim, 'is-opening');
      mega.trigger = trigger;
      mega.mode = mode;
      mega.scrollY = window.scrollY;
    } else if (mode === 'pinned') {
      mega.mode = 'pinned';
    }
    if (focus) {
      const links = [...panel.querySelectorAll('a[href]')].filter(link => link.getClientRects().length);
      (focus === 'last' ? links[links.length - 1] : links[0])?.focus({ preventScroll: true });
    }
  }
  function megaClose({ restoreFocus = false, animate = true } = {}) {
    clearOpenTimer();
    clearCloseTimer();
    const trigger = mega.trigger;
    if (!trigger) return;
    const panel = panelFor(trigger);
    const owns = node => !!node && (node === trigger || !!panel?.contains(node));
    // Focus is on the trigger or in the panel, or was until CSS hid them (see headerFocus).
    const focusWasInside = owns(document.activeElement) || (focusLost() && owns(headerFocus));
    mega.trigger = null;
    mega.mode = null;
    trigger.setAttribute('aria-expanded', 'false');
    if (panel) hideLayer(panel, animate);
    const scrim = megaScrim();
    if (scrim) hideLayer(scrim, animate);
    if (restoreFocus && focusWasInside) focusTrigger(trigger);
  }
  const scheduleClose = delay => {
    clearCloseTimer();
    mega.closeTimer = setTimeout(() => { if (mega.mode === 'hover') megaClose(); }, delay);
  };
  // The scrim (and the panel's max-height) start at the header's bottom edge, which moves while the announcement
  // bar above is in view. Set on the section wrapper, the parent of both the header and the scrim.
  const placeMega = trigger => {
    const header = trigger?.closest('[data-hb-header]');
    if (header?.parentElement) header.parentElement.style.setProperty('--hb-mega-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
  };
  // Focus goes back to the trigger, or to the Menu button when the trigger is hidden (bar overflow, < 1000px).
  const focusTrigger = trigger => {
    const target = trigger.getClientRects().length ? trigger
      : [...document.querySelectorAll('[data-hb-header] [data-hb-menu-open]')].find(button => button.getClientRects().length);
    target?.focus({ preventScroll: true });
  };
  const isMouse = event => (event.pointerType === 'mouse' || event.pointerType === 'pen') && finePointer.matches && desktop.matches;

  document.addEventListener('pointerover', event => {
    if (!isMouse(event) || !(event.target instanceof Element)) return;
    const openPanel = panelFor(mega.trigger);
    if (openPanel?.contains(event.target)) { clearCloseTimer(); clearOpenTimer(); return; }
    if (event.target.closest('[data-hb-mega-scrim]')) {
      clearOpenTimer();
      if (mega.mode === 'hover' && !mega.closeTimer) scheduleClose(CLOSE_DELAY);
      return;
    }
    const header = event.target.closest('[data-hb-header]');
    if (!header) return;
    if (mega.mode === 'hover' && !header.contains(event.relatedTarget)) clearCloseTimer();
    const item = event.target.closest('.hb-header__nav-link');
    if (!item || item.contains(event.relatedTarget)) return;
    if (item.matches('[data-hb-mega-trigger]')) {
      prewarm(panelFor(item));
      clearCloseTimer();
      clearOpenTimer();
      if (mega.trigger === item) return;
      const mode = mega.mode === 'pinned' ? 'pinned' : 'hover';
      mega.openTimer = setTimeout(() => megaOpen(item, { mode }), OPEN_DELAY);
    } else {
      clearOpenTimer();
      if (mega.mode === 'hover') scheduleClose(LINK_CLOSE_DELAY);
    }
  });
  document.addEventListener('pointerout', event => {
    if (!isMouse(event) || !(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-hb-mega-trigger]');
    if (trigger && mega.trigger !== trigger && !trigger.contains(event.relatedTarget)) clearOpenTimer();
    const header = event.target.closest('[data-hb-header]');
    if (header && !header.contains(event.relatedTarget)) {
      clearOpenTimer();
      if (mega.mode === 'hover') scheduleClose(CLOSE_DELAY);
    }
  });
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-hb-mega-scrim]')) { megaClose(); return; }
    const trigger = event.target.closest('[data-hb-mega-trigger]');
    if (!trigger) return;
    event.preventDefault();
    if (mega.trigger !== trigger || mega.mode === 'hover') megaOpen(trigger, { mode: 'pinned' });
    else megaClose();
  });
  document.addEventListener('pointerdown', event => {
    headerFocus = null;
    if (!mega.trigger || !(event.target instanceof Element)) return;
    if (event.target.closest('[data-hb-header], [data-hb-mega-scrim]')) return;
    megaClose();
  }, { passive: true });
  document.addEventListener('focusin', event => {
    if (!(event.target instanceof Element)) return;
    headerFocus = event.target.closest('[data-hb-header]') ? event.target : null;
    if (event.target.matches('[data-hb-mega-trigger]')) prewarm(panelFor(event.target));
  });
  document.addEventListener('focusout', event => {
    if (!(event.relatedTarget instanceof Element)) return;
    if (!event.relatedTarget.closest('[data-hb-header]')) headerFocus = null;
    if (!mega.trigger) return;
    const item = mega.trigger.closest('.hb-header__nav-item');
    if (!item || !item.contains(event.target) || item.contains(event.relatedTarget)) return;
    megaClose();
  });

  const barItems = () => [...document.querySelectorAll('[data-hb-header] .hb-header__nav-item')]
    .filter(item => item.getClientRects().length)
    .map(item => item.querySelector('.hb-header__nav-link'))
    .filter(Boolean);
  document.addEventListener('keydown', event => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || !(event.target instanceof Element)) return;
    if (event.key === 'Escape') {
      if (!mega.trigger) return;
      event.preventDefault();
      megaClose({ restoreFocus: true });
      return;
    }
    const barLink = event.target.closest('[data-hb-header] .hb-header__nav-link');
    if (barLink) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!barLink.matches('[data-hb-mega-trigger]')) return;
        event.preventDefault();
        megaOpen(barLink, { mode: 'pinned', focus: event.key === 'ArrowDown' ? 'first' : 'last' });
        return;
      }
      const items = barItems();
      const index = items.indexOf(barLink);
      if (index === -1) return;
      const next = { ArrowRight: items[(index + 1) % items.length], ArrowLeft: items[(index - 1 + items.length) % items.length], Home: items[0], End: items[items.length - 1] }[event.key];
      if (!next) return;
      event.preventDefault();
      if (mega.trigger) {
        if (next.matches('[data-hb-mega-trigger]')) megaOpen(next, { mode: 'pinned' });
        else megaClose({ animate: false });
      }
      next.focus();
      return;
    }
    const panel = event.target.closest('[data-hb-mega]');
    if (!panel || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
    const column = event.target.closest('.hb-mega__col') || panel;
    const links = [...column.querySelectorAll('a[href]')].filter(link => link.getClientRects().length);
    const index = links.indexOf(event.target.closest('a[href]'));
    if (index === -1) return;
    event.preventDefault();
    if (event.key === 'ArrowUp' && index === 0) { mega.trigger?.focus(); return; }
    links[Math.min(links.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))].focus();
  });

  // The page scrolling under an open panel closes it (scrolls inside the panel do not reach window).
  window.addEventListener('scroll', () => {
    if (mega.trigger && Math.abs(window.scrollY - mega.scrollY) > SCROLL_CLOSE) megaClose({ restoreFocus: true });
  }, { passive: true });
  // Another dialog (drawer, search incl. ⌘K, bag) takes over the screen. When it was opened from a link inside
  // the panel (⌘K with focus there), it returns focus to the panel's trigger, not to the link hidden here. (The
  // panel may already be closed: showModal moving focus out of it fires focusout first.)
  document.addEventListener('hb:open', () => {
    const panel = lastTrigger instanceof Element ? lastTrigger.closest('[data-hb-mega]') : null;
    if (panel) lastTrigger = document.querySelector(`[data-hb-mega-trigger][aria-controls="${panel.id}"]`) || lastTrigger;
    megaClose({ animate: false });
  }, true);
  // A focused element that CSS just hid (or that Chrome already blurred to <body> for it) hands focus to the
  // visible Menu button; focusTrigger() falls back to it when the element has no box.
  const rescueHeaderFocus = () => {
    const hidden = headerFocus && !headerFocus.getClientRects().length ? headerFocus : null;
    if (hidden && (focusLost() || document.activeElement === hidden)) focusTrigger(hidden);
  };
  // Narrowing the window can hide a bar link (the fit rules are container queries from snippets/humbler-nav-fit):
  // close its panel and hand focus to a visible control. Otherwise keep the panel under the header.
  window.addEventListener('resize', () => {
    if (mega.trigger && !mega.trigger.getClientRects().length) megaClose({ animate: false, restoreFocus: true });
    else if (mega.trigger) placeMega(mega.trigger);
    rescueHeaderFocus();
  }, { passive: true });
  // Below 1000px the bar, panel and scrim are display:none: close at once, focus moves to the mobile Menu
  // button. Going up, the mobile Menu button hides and focus moves to the desktop one.
  const onDesktopChange = () => {
    if (!desktop.matches) megaClose({ animate: false, restoreFocus: true });
    rescueHeaderFocus();
  };
  if (desktop.addEventListener) desktop.addEventListener('change', onDesktopChange);
  else desktop.addListener?.(onDesktopChange);
  window.addEventListener?.('pageshow', event => { if (event.persisted) megaClose({ animate: false }); });
  // Theme editor: the header section is re-rendered; drop state that points at the old elements.
  const resetMega = event => {
    if (!event.target.querySelector?.('[data-hb-header]')) return;
    clearOpenTimer();
    clearCloseTimer();
    mega.trigger = null;
    mega.mode = null;
    headerFocus = null;
  };
  document.addEventListener('shopify:section:unload', resetMega);
  document.addEventListener('shopify:section:load', resetMega);

  /* Search lives in assets/humbler-search.js (snippets/humbler-search.liquid). */

  /* ---------- bag ---------- */
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-hb-cart-open]');
    if (!opener || !window.HumblerCart || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    window.HumblerCart.open(opener);
  });
  document.addEventListener('humbler:cart:updated', event => {
    const count = Number(event.detail?.item_count ?? 0);
    document.querySelectorAll('[data-hb-cart-count]').forEach(badge => {
      const changed = badge.textContent !== String(count);
      badge.textContent = count;
      badge.hidden = count === 0;
      if (changed && count > 0 && !reduceMotion.matches) { badge.classList.remove('is-bumped'); void badge.offsetWidth; badge.classList.add('is-bumped'); }
    });
    document.querySelectorAll('[data-hb-cart-count-label]').forEach(label => { label.textContent = `${count} ${count === 1 ? 'item' : 'items'} in cart`; });
  });
})();
